export default function PrivacyPolicyPage() {
    return (
        <div className="max-w-4xl mx-auto px-4 py-8 pb-safe">
            <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
            <p className="text-default-500 mb-8">Last Updated: November 27, 2025</p>

            <div className="space-y-8">
                <section>
                    <p className="text-default-700">
                        NEW WAY SPORT d.o.o. ("we," "our," or "us") operates a mobile application (the "App") for membership management. This Privacy Policy explains how we collect, use, disclose, and safeguard your information.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold mb-4">1. Information We Collect</h2>

                    <h3 className="text-xl font-medium mb-3">Personal Information You Provide:</h3>
                    <ul className="list-disc pl-6 space-y-2 text-default-700 mb-4">
                        <li>Email address</li>
                        <li>Password (stored encrypted)</li>
                        <li>Profile photo</li>
                        <li>Date of birth</li>
                        <li>Address information</li>
                        <li>Golf profile details</li>
                        <li>Membership status</li>
                    </ul>

                    <h3 className="text-xl font-medium mb-3">Automatically Collected Information:</h3>
                    <ul className="list-disc pl-6 space-y-2 text-default-700">
                        <li>Device ID (for error tracking and troubleshooting)</li>
                        <li>Error logs and crash reports</li>
                        <li>App usage data for technical diagnostics</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold mb-4">2. How We Use Your Information</h2>
                    <p className="text-default-700 mb-3">We use your information to:</p>
                    <ul className="list-disc pl-6 space-y-2 text-default-700">
                        <li>Create and manage your membership account</li>
                        <li>Verify your identity and membership status</li>
                        <li>Communicate with you about membership updates and announcements</li>
                        <li>Troubleshoot technical issues and improve app functionality</li>
                        <li>Send push notifications (when implemented) regarding announcements and membership updates</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold mb-4">3. Data Storage and Security</h2>
                    <ul className="list-disc pl-6 space-y-2 text-default-700">
                        <li>Your data is stored on secure servers located in the Netherlands (Scaleway)</li>
                        <li>All data is encrypted in transit (HTTPS) and at rest</li>
                        <li>We implement industry-standard security measures to protect your information</li>
                        <li>Access to personal data is restricted to authorized personnel only</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold mb-4">4. Third-Party Services</h2>
                    <p className="text-default-700 mb-4">We use the following third-party services that may collect or process your data:</p>

                    <div className="space-y-3 text-default-700">
                        <p>
                            <strong>Sentry:</strong> For error tracking and crash reporting. Sentry may collect device information, error logs, and technical data.{' '}
                            <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                Sentry Privacy Policy
                            </a>
                        </p>
                        <p>
                            <strong>Scaleway:</strong> For data storage and email services. Data is stored in the Netherlands.{' '}
                            <a href="https://www.scaleway.com/en/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                Scaleway Privacy Policy
                            </a>
                        </p>
                    </div>

                    <p className="text-default-700 mt-4">We do not sell, trade, or rent your personal information to third parties.</p>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold mb-4">5. App Permissions</h2>
                    <p className="text-default-700 mb-3">The App requests the following permissions:</p>
                    <ul className="list-disc pl-6 space-y-2 text-default-700">
                        <li><strong>Camera:</strong> To capture profile photos</li>
                        <li><strong>Photo Library/File System:</strong> To upload existing photos as profile pictures</li>
                        <li><strong>Push Notifications:</strong> (Future) To send membership updates and announcements</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold mb-4">6. Data Sharing</h2>
                    <p className="text-default-700">
                        We do not share your personal information with other app users. Your profile and information are only visible to you and authorized administrators of your association.
                    </p>
                    <p className="text-default-700 mt-3">
                        We may disclose your information if required by law or to protect our rights, safety, or property.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold mb-4">7. Data Retention</h2>
                    <p className="text-default-700">
                        We retain your personal information for as long as your account is active or as needed to provide services. If you wish to delete your account, please contact your association administrators or email us at{' '}
                        <a href="mailto:info@nws.si" className="text-primary hover:underline">info@nws.si</a>.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold mb-4">8. Your Rights</h2>
                    <p className="text-default-700 mb-3">Depending on your location, you may have the following rights:</p>
                    <ul className="list-disc pl-6 space-y-2 text-default-700">
                        <li>Access your personal data</li>
                        <li>Correct inaccurate data</li>
                        <li>Request deletion of your data</li>
                        <li>Object to data processing</li>
                        <li>Data portability</li>
                    </ul>
                    <p className="text-default-700 mt-4">
                        To exercise these rights, contact your association administrators or email{' '}
                        <a href="mailto:info@nws.si" className="text-primary hover:underline">info@nws.si</a>.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold mb-4">9. GDPR Compliance (EU Users)</h2>
                    <p className="text-default-700 mb-3">
                        As we operate in the EU and store data in the Netherlands, we comply with the General Data Protection Regulation (GDPR). You have rights under GDPR including access, rectification, erasure, and data portability.
                    </p>
                    <p className="text-default-700">
                        Legal basis for processing: Performance of contract and legitimate interests in providing membership services.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold mb-4">10. Children's Privacy</h2>
                    <p className="text-default-700">
                        Our App is not directed to children under 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us at{' '}
                        <a href="mailto:info@nws.si" className="text-primary hover:underline">info@nws.si</a>.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold mb-4">11. International Data Transfers</h2>
                    <p className="text-default-700">
                        Your data is stored in the Netherlands (EU). If you access the App from outside the EU, your information will be transferred to and stored in the EU with appropriate safeguards.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold mb-4">12. Changes to This Privacy Policy</h2>
                    <p className="text-default-700">
                        We may update this Privacy Policy from time to time. We will notify you of changes by updating the "Last Updated" date and, where appropriate, through in-app notifications.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold mb-4">13. Contact Us</h2>
                    <p className="text-default-700 mb-2">If you have questions about this Privacy Policy, please contact us:</p>
                    <div className="text-default-700">
                        <p className="font-semibold">NEW WAY SPORT d.o.o.</p>
                        <p>Koštialova ulica 31</p>
                        <p>8000 Novo mesto, Slovenia</p>
                        <p>
                            Email: <a href="mailto:info@nws.si" className="text-primary hover:underline">info@nws.si</a>
                        </p>
                    </div>
                </section>
            </div>
        </div>
    );
}